// Auth helper — typedefs + normalizers for auth.service.js.
//
// Every normalizer lists all @typedef fields explicitly; no ...raw spread.

import { ROUTES } from 'src/router/const'
import { notifyMsgs } from 'src/services/app/notify.service'
import {
  PLAN_STATUS,
  PLAN_TONE_CLASS,
  TRIAL_EXPIRING_SOON_DAYS,
} from 'src/services/consts/account.const'
import { REMOVE_ACCOUNT_MEMBER_ERROR_CODE } from 'src/services/consts/account-members.const'
import { API_ERRORS } from 'src/services/consts/api.const'
import {
  AUTH0_ERROR_CODE,
  AUTH0_ERROR_DESCRIPTION,
  AUTH_ERROR_REASON,
  AUTH_SCREEN_COPY,
  AUTH_SESSION_ERROR_CODE,
  AUTH_SESSION_EXPIRED_ORG_ENDED_SUBLINE,
  EMAIL_VERIFICATION_STATES,
  GATE_PANELS,
  INVITE_VERIFICATION_REASON,
} from 'src/services/consts/app.const'
import { EMAIL_PREFERENCE_KEYS } from 'src/services/consts/settings.const'
import { formatDateByFormat, getPlural, isSameEmail } from 'src/services/helpers/util.helper'

/**
 * @typedef {Object} EmailPreferences
 * @property {boolean} scanFailed
 * @property {boolean} scanSuccess
 * @property {boolean} scanMonthly
 */

/**
 * @typedef {Object} UpdateAccountPayload
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {boolean} [onboardingDone]
 * @property {Partial<EmailPreferences>} [emailPreferences]
 */

/**
 * @typedef {Object} EmailPreferencesPatch
 * @property {Partial<EmailPreferences>} emailPreferences
 */

/**
 * @typedef {Object} AccountData
 * @property {string} userId
 * @property {string|null} accountId
 * @property {string|null} accountName
 * @property {string|null} companyName
 * @property {string|null} country
 * @property {string|null} email
 * @property {string|null} firstName
 * @property {string|null} lastName
 * @property {boolean} isActive
 * @property {string|null} lastLoginAt
 * @property {string|null} role
 * @property {string|null} planStatus - a PLAN_STATUS value or null
 * @property {string|null} trialExpiresAt
 * @property {boolean} usingPasswordForAuth
 * @property {boolean} onboardingDone
 * @property {EmailPreferences|null} emailPreferences
 */

/**
 * @typedef {Object} AccountProfileResponse
 * @property {string} accountId
 * @property {string} accountName
 * @property {string|null} companyName
 * @property {string|null} country
 */

/** @returns {AccountProfileResponse|null} */
export function normalizeAccountProfileResponse(raw) {
  if (!raw || typeof raw !== 'object' || !raw.accountId || raw.accountName == null) return null
  return {
    accountId: raw.accountId,
    accountName: String(raw.accountName).trim(),
    companyName: raw.companyName ?? null,
    country: raw.country ?? null,
  }
}

/**
 * @typedef {Object} DeleteAccountResponse
 * @property {string} accountId
 * @property {boolean} active
 * @property {string} deletedAt
 */

/** @returns {DeleteAccountResponse|null} */
export function normalizeDeleteAccountResponse(raw) {
  if (!raw || typeof raw !== 'object' || !raw.accountId || raw.deletedAt == null) return null
  return {
    accountId: raw.accountId,
    active: Boolean(raw.active),
    deletedAt: raw.deletedAt,
  }
}

/**
 * User-facing notify message for delete-account failures.
 * Maps HTTP 403 (non-admin) to a specific toast; defaults to a generic delete error.
 * Authorization is enforced by the API — this is messaging only.
 * @param {Error & { response?: { status?: number } }} error
 * @returns {string}
 */
export function resolveDeleteAccountErrorMessage(error) {
  if (error?.response?.status === 403) {
    return notifyMsgs.error.deleteAccountForbidden
  }
  return notifyMsgs.error.deleteAccount
}

/**
 * @typedef {Object} LeaveAccountResponse
 * @property {boolean} left
 */

/** @returns {LeaveAccountResponse|null} */
export function normalizeLeaveAccountResponse(raw) {
  if (raw == null || raw === '') return { left: true }
  if (typeof raw === 'object' && !Array.isArray(raw)) return { left: true }
  return null
}

/**
 * User-facing notify message for leave-account failures.
 * Maps HTTP 409 `last_admin` to a specific toast; defaults to a generic leave error.
 * @param {Error & { response?: { status?: number, data?: { error?: string|{ code?: string } } } }} error
 * @returns {string}
 */
export function resolveLeaveAccountErrorMessage(error) {
  if (
    error?.response?.status === 409 &&
    getResponseErrorCode(error.response) === REMOVE_ACCOUNT_MEMBER_ERROR_CODE.LAST_ADMIN
  ) {
    return notifyMsgs.error.leaveAccountLastAdmin
  }
  return notifyMsgs.error.leaveAccount
}

// Auth0 has no real name to store for a password signup, so it sets the profile
// `name` to the email address — which reaches /me as a first name. Treat a name
// that is just the email as absent, so onboarding asks for a real one instead of
// prefilling an address and every display surface stays clean.
function _rejectEmailAsName(name, email) {
  if (!name) return null
  return isSameEmail(name, email) ? null : name
}

// --- Email preferences ---------------------------------------------------

/**
 * @param {unknown} raw
 * @returns {EmailPreferences|null}
 */
export function normalizeEmailPreferences(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null

  return {
    scanFailed: raw.scanFailed,
    scanSuccess: raw.scanSuccess,
    scanMonthly: raw.scanMonthly,
  }
}

/**
 * @param {EmailPreferences} preferences
 * @returns {boolean}
 */
export function deriveAllowAllEmailPreferences(preferences) {
  return EMAIL_PREFERENCE_KEYS.every((key) => preferences[key] === true)
}

/**
 * @param {EmailPreferences|null|undefined} preferences
 * @returns {EmailPreferences & { allowAll: boolean }}
 */
export function buildEmailPreferencesForm(preferences) {
  const source = preferences ?? Object.fromEntries(EMAIL_PREFERENCE_KEYS.map((key) => [key, false]))
  return {
    allowAll: deriveAllowAllEmailPreferences(source),
    ...Object.fromEntries(EMAIL_PREFERENCE_KEYS.map((key) => [key, source[key]])),
  }
}

/**
 * @param {{ saved: EmailPreferences, draft: EmailPreferences }} params
 * @returns {EmailPreferencesPatch|null}
 */
export function buildEmailPreferencesPatch({ saved, draft }) {
  /** @type {Partial<EmailPreferences>} */
  const changed = {}

  for (const key of EMAIL_PREFERENCE_KEYS) {
    if (saved[key] !== draft[key]) changed[key] = draft[key]
  }

  if (Object.keys(changed).length === 0) return null
  return { emailPreferences: changed }
}

/** @returns {AccountData|null} */
export function normalizeAccountData(raw) {
  if (!raw || typeof raw !== 'object' || !raw.userId) return null
  const email = raw.email ?? null
  return {
    userId: raw.userId,
    accountId: raw.accountId ?? null,
    accountName: raw.accountName ?? null,
    companyName: raw.companyName ?? null,
    country: raw.country ?? null,
    email,
    firstName: _rejectEmailAsName(raw.firstName, email),
    lastName: _rejectEmailAsName(raw.lastName, email),
    isActive: Boolean(raw.isActive),
    lastLoginAt: raw.lastLoginAt ?? null,
    role: raw.role ?? null,
    planStatus: raw.planStatus ?? null,
    trialExpiresAt: raw.trialExpiresAt ?? null,
    usingPasswordForAuth: Boolean(raw.usingPasswordForAuth),
    onboardingDone: Boolean(raw.onboardingDone),
    emailPreferences: normalizeEmailPreferences(raw.emailPreferences),
  }
}

// --- Trial status --------------------------------------------------------

/**
 * @param {string|null|undefined} trialExpiresAt
 * @returns {number|null}
 */
export function computeTrialDaysLeft(trialExpiresAt) {
  if (!trialExpiresAt) return null
  const expiresAt = new Date(trialExpiresAt)
  if (Number.isNaN(expiresAt.getTime())) return null
  return Math.ceil((expiresAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
}

/**
 * @param {{ trialExpiresAt?: string|null }} params
 * @returns {boolean}
 */
export function isTrialExpiringSoon({ trialExpiresAt }) {
  const daysLeft = computeTrialDaysLeft(trialExpiresAt)
  return daysLeft !== null && daysLeft <= TRIAL_EXPIRING_SOON_DAYS && daysLeft > 0
}

/**
 * @param {string|null|undefined} trialExpiresAt
 * @returns {string|null} e.g. "12 days left", or null when missing / already elapsed
 */
export function formatTrialDaysLeft(trialExpiresAt) {
  const daysLeft = computeTrialDaysLeft(trialExpiresAt)
  if (daysLeft == null || daysLeft <= 0) return null
  return `${daysLeft} ${getPlural('day', daysLeft)} left`
}

/**
 * Date-based expiry for an active trial plan. Missing dates do not count as
 * expired — only a parseable `trialExpiresAt` that has already elapsed.
 *
 * @param {{ planStatus?: string|null, trialExpiresAt?: string|null }} params
 * @returns {boolean}
 */
export function isTrialExpiredByDate({ planStatus, trialExpiresAt }) {
  if (planStatus !== PLAN_STATUS.TRIAL) return false
  const daysLeft = computeTrialDaysLeft(trialExpiresAt)
  return daysLeft !== null && daysLeft <= 0
}

/**
 * Axios-shaped 401 `{ error: 'trial_expired' }` — same contract as a real
 * backend rejection (`httpError(401, API_ERRORS.TRIAL_EXPIRED)`).
 *
 * @param {{ url?: string }} [config] axios request config (or `{ url }`)
 * @returns {Error & { response: { status: number, data: { error: string } }, config?: { url?: string } }}
 */
export function createTrialExpiredHttpError(config) {
  const error = new Error('Request failed with status code 401')
  error.response = {
    status: 401,
    data: { error: API_ERRORS.TRIAL_EXPIRED },
  }
  if (config) {
    error.config = config
  }
  return error
}

/**
 * @param {{ error?: string, response?: { data?: { error?: string } } }} error
 * @returns {boolean}
 */
export function isTrialExpiredApiError(error) {
  const errorCode = error?.error ?? error?.response?.data?.error
  return errorCode === API_ERRORS.TRIAL_EXPIRED
}

/**
 * @typedef {Object} PlanBadge
 * @property {string} label
 * @property {string|null} subline
 * @property {string} toneClass
 * @property {string} icon
 */

/**
 * @param {{ planStatus?: string|null, trialExpiresAt?: string|null, error?: { error?: string, response?: { data?: { error?: string } } } }} params
 * @returns {PlanBadge|null}
 */
export function getPlanBadge({ planStatus, trialExpiresAt, error }) {
  if (isTrialExpiredApiError(error)) return null

  if (planStatus === PLAN_STATUS.ACTIVE) {
    return {
      label: 'Active',
      subline: null,
      toneClass: PLAN_TONE_CLASS.ACTIVE,
      icon: 'circle-check',
    }
  }

  if (planStatus !== PLAN_STATUS.TRIAL) return null

  const daysLeftLabel = formatTrialDaysLeft(trialExpiresAt)
  const formattedDate = formatDateByFormat(trialExpiresAt, 'MMM d, yyyy')
  const isExpiringSoon = isTrialExpiringSoon({ trialExpiresAt })
  const subline = [daysLeftLabel, formattedDate ? `Ends on ${formattedDate}` : null]
    .filter(Boolean)
    .join(' · ')

  return {
    label: 'Trial',
    subline: subline || null,
    toneClass: isExpiringSoon ? PLAN_TONE_CLASS.EXPIRING_SOON : PLAN_TONE_CLASS.DEFAULT,
    icon: isExpiringSoon ? 'alert-triangle' : 'clock',
  }
}

/**
 * @typedef {Object} InviteVerification
 * @property {boolean} valid
 * @property {string|null} accountName
 * @property {string|null} email
 * @property {string|null} role
 * @property {string|null} invitedByEmail
 * @property {string|null} reason - an INVITE_VERIFICATION_REASON value
 */

/**
 * @param {string} [reason] - an INVITE_VERIFICATION_REASON value
 * @returns {InviteVerification}
 */
export function createInvalidInviteVerification(reason = INVITE_VERIFICATION_REASON.INVALID) {
  return {
    valid: false,
    accountName: null,
    email: null,
    role: null,
    invitedByEmail: null,
    reason,
  }
}

/** Statuses that mean the backend judged the token itself, not that it was unreachable. */
const TOKEN_REJECTED_STATUSES = [400, 404]

/**
 * Classify a failed GET /invites/{token} so the landing can tell "this token is
 * dead" apart from "we could not check it".
 *
 * Only a 4xx that rejects the token is a verdict. Everything else — network
 * failure, timeout, 5xx, an unexpected 401/403 that would mean the public
 * endpoint stopped being public — leaves the token's real state unknown, and
 * must not tell the invitee their invitation expired.
 *
 * @param {{ response?: { status?: number } }} error
 * @returns {string} An INVITE_VERIFICATION_REASON value
 */
export function resolveInviteVerificationFailureReason(error) {
  return TOKEN_REJECTED_STATUSES.includes(error?.response?.status)
    ? INVITE_VERIFICATION_REASON.INVALID
    : INVITE_VERIFICATION_REASON.UNAVAILABLE
}

/** @returns {InviteVerification} */
export function normalizeInviteVerification(raw) {
  if (!raw || typeof raw !== 'object') {
    return createInvalidInviteVerification()
  }
  return {
    valid: Boolean(raw.valid),
    accountName: raw.accountName ?? null,
    email: raw.email ?? null,
    role: raw.role ?? null,
    invitedByEmail: raw.invitedByEmail ?? null,
    reason: raw.reason ?? null,
  }
}

// --- Onboarding classification -------------------------------------------
//
// Signup and invite-acceptance are the SAME Auth0 login, so the two outcomes
// can only be told apart AFTER login, from GET /me. resolveOnboardingStep is
// the single source of truth for which SessionGate panel a user belongs on.
//
// A founder of a brand-new account is auto-provisioned by the backend with a
// NULL accountName (they name it via POST /account/setup). An invited member
// joins an account an admin already named, so accountName is set. `onboardingDone`
// decides *whether* to gate; accountName decides *which* panel.

/**
 * Decide which onboarding gate step (if any) an authenticated user belongs on,
 * from their /me data.
 *
 * Returns FOUNDER_WELCOME / INVITEE_WELCOME, or null when onboarding is complete.
 * Never returns a session panel — those are owned by SessionGate.
 *
 * @param {import('./auth.helper').AccountData|null} accountData
 * @returns {string|null}
 */
export function resolveOnboardingStep(accountData) {
  if (!accountData?.accountId) return null

  // Tested before onboardingDone: FounderWelcomePanel completes onboarding and
  // then renames in two requests, so an unnamed account can briefly carry
  // onboardingDone true and must keep resolving here until the rename lands.
  if (!accountData.accountName?.trim()) return GATE_PANELS.FOUNDER_WELCOME

  if (!accountData.onboardingDone) return GATE_PANELS.INVITEE_WELCOME

  return null
}

const PUBLIC_ROUTE_PANELS = {
  [ROUTES.ACCEPT_INVITE]: GATE_PANELS.INVITE_SESSION,
  [ROUTES.EMAIL_VERIFIED]: GATE_PANELS.EMAIL_VERIFIED_SESSION,
}

/**
 * Map a public route name to the panel an already-authenticated visitor should
 * see on that landing. Returns null for any other route name (including undefined).
 *
 * @param {string|undefined|null} routeName
 * @returns {string|null}
 */
export function resolvePublicRoutePanel(routeName) {
  return PUBLIC_ROUTE_PANELS[routeName] ?? null
}

/**
 * Read a machine-readable error code from an axios response body.
 * Supports string `error` or object `{ code: string }` — not nested `error.error`.
 * @param {{ data?: { error?: string|{ code?: string } } }} response
 * @returns {string}
 */
export function getResponseErrorCode(response) {
  const errorField = response?.data?.error
  if (typeof errorField === 'string') return errorField
  if (typeof errorField === 'object' && typeof errorField?.code === 'string') {
    return errorField.code
  }
  return ''
}

// Exported: reused by src/boot/auth0.js's guard to skip capturing this
// expected user state as an error.
export function isEmailNotVerifiedAuthError(authError) {
  return (
    authError?.error === AUTH0_ERROR_CODE.ACCESS_DENIED &&
    authError?.error_description === AUTH0_ERROR_DESCRIPTION.EMAIL_NOT_VERIFIED
  )
}

export function isAccountLockedAuthError(authError) {
  return (
    authError?.error === AUTH0_ERROR_CODE.ACCESS_DENIED &&
    authError?.error_description === AUTH0_ERROR_DESCRIPTION.ACCOUNT_LOCKED
  )
}

/**
 * Whether an authenticated session has a terminal GET /me failure (401, 404)
 * or an error with no account payload.
 * @returns {boolean}
 */
export function hasAccountLoadError({ isAuthenticated, accountError, accountData }) {
  if (!isAuthenticated || !accountError) return false

  const status = accountError?.response?.status
  if (status === 401 || status === 404) return true

  return !accountData
}

function resolveAuthErrorReason({ authError, accountError }) {
  if (isEmailNotVerifiedAuthError(authError)) {
    return AUTH_ERROR_REASON.EMAIL_NOT_VERIFIED
  }

  if (isAccountLockedAuthError(authError)) {
    return AUTH_ERROR_REASON.ACCOUNT_LOCKED
  }

  if (authError) {
    return AUTH_ERROR_REASON.AUTH0
  }

  const status = accountError?.response?.status

  if (status === 404) {
    return AUTH_ERROR_REASON.NOT_PROVISIONED
  }

  if (status === 401) {
    if (getResponseErrorCode(accountError.response) === AUTH_SESSION_ERROR_CODE.ACCOUNT_LOCKED) {
      return AUTH_ERROR_REASON.ACCOUNT_LOCKED
    }
    if (isTrialExpiredApiError(accountError)) {
      return AUTH_ERROR_REASON.TRIAL_EXPIRED
    }
    return AUTH_ERROR_REASON.SESSION_EXPIRED
  }

  return AUTH_ERROR_REASON.BOOTSTRAP_FAILED
}

/**
 * Copy for the session error panel. SessionGate has already decided this IS an
 * error, so this only classifies which one.
 */
export function resolveAuthErrorCopy({ authError, accountError, accountData, isAuthenticated }) {
  const hasAccountError = hasAccountLoadError({ isAuthenticated, accountError, accountData })
  const activeError = authError || (hasAccountError ? accountError : null)

  // Leftover elapsed trial is a gate even when GET /me last succeeded (no query error).
  if (!authError && isTrialExpiredByDate(accountData ?? {})) {
    return AUTH_SCREEN_COPY[AUTH_ERROR_REASON.TRIAL_EXPIRED]
  }

  const errorReason = resolveAuthErrorReason({
    authError,
    accountError: activeError,
  })

  const screenCopy = AUTH_SCREEN_COPY[errorReason]
  if (errorReason === AUTH_ERROR_REASON.SESSION_EXPIRED && isOrgEndedSessionError(activeError)) {
    return {
      ...screenCopy,
      subline: [...screenCopy.subline, AUTH_SESSION_EXPIRED_ORG_ENDED_SUBLINE],
    }
  }

  return screenCopy
}

function isOrgEndedSessionError(accountError) {
  const errorCode = getResponseErrorCode(accountError?.response)
  return (
    errorCode === AUTH_SESSION_ERROR_CODE.MEMBER_REMOVED ||
    errorCode === AUTH_SESSION_ERROR_CODE.ACCOUNT_DELETED
  )
}

// --- Email verification result --------------------------------------------

/** Auth0's phrasing for a link that was already consumed ("This URL can be used only once"). */
const ALREADY_VERIFIED_MESSAGE_PATTERN =
  /already\s+(been\s+)?verified|used\s+only\s+once|already\s+used/i

/** Coerce a `route.query` value (string | string[] | absent) to a trimmed string. */
function _readQueryValue(raw) {
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * Classify the result Auth0 appends to the verification email template's
 * "Redirect To" URL (`?success=<bool>&message=<text>&code=<text>`).
 *
 * `success=false` with an already-used message is the common double-click /
 * mail-client prefetch case: the address IS verified by then, so it must not
 * read as an error.
 *
 * Matching on `message` text is the only discriminator available — Auth0 does
 * not reliably send a machine-readable code on failures — so anything
 * unclassified falls back to INVALID, which still offers sign-in and is
 * therefore safe.
 *
 * @param {{ success: string|string[]|null|undefined, message: string|string[]|null|undefined }} params
 * @returns {string} An EMAIL_VERIFICATION_STATES value
 */
export function resolveEmailVerificationState({ success, message }) {
  const successValue = _readQueryValue(success)

  // Opened directly, refreshed, or bookmarked — there is no result to report.
  if (!successValue) return EMAIL_VERIFICATION_STATES.NO_RESULT

  if (successValue === 'true') return EMAIL_VERIFICATION_STATES.VERIFIED

  if (ALREADY_VERIFIED_MESSAGE_PATTERN.test(_readQueryValue(message))) {
    return EMAIL_VERIFICATION_STATES.ALREADY_VERIFIED
  }

  return EMAIL_VERIFICATION_STATES.INVALID
}
